import { validationPatterns } from '../../../../config/ui/forms/form-config';
import '../../../../config/ui/forms/form-styles.css';

interface FormProgressProps {
  formData: Record<string, string>;
  errors: Record<string, string>;
  requiredFields: string[];
  optionalFields: string[];
}

export function FormProgress({ formData, errors, requiredFields, optionalFields }: FormProgressProps) {
  // Calculate progress (same logic as create modal)
  let completedRequired = 0;
  let completedOptional = 0;
  
  // Check required fields
  requiredFields.forEach(field => {
    const value = formData[field];
    if (value && value.trim()) {
      // Check if field is valid
      if (field === 'email' && !validationPatterns.email.test(value)) {
        return;
      }
      completedRequired++;
    }
  });
  
  // Check optional fields
  optionalFields.forEach(field => {
    const value = formData[field];
    if (value && value.trim()) {
      // Check if field is valid (phone validation if needed)
      if (field === 'phone_number' && value) {
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly && !validationPatterns.phone.test(digitsOnly)) {
          return;
        }
      }
      completedOptional++;
    }
  });
  
  // Calculate progress: 70% for required fields, 30% for optional
  const requiredProgress = (completedRequired / requiredFields.length) * 70;
  const optionalProgress = optionalFields.length > 0 
    ? (completedOptional / optionalFields.length) * 30 
    : 0;
  const totalProgress = requiredProgress + optionalProgress;
  
  // Form is complete when all required fields are valid and there are no errors
  const activeErrors = Object.entries(errors).filter(([_, error]) => error);
  const isComplete = completedRequired === requiredFields.length && activeErrors.length === 0;

  return (
    <>
      {/* Progress Bar */}
      <div className="app-form-progress">
        <div className="app-form-progress-header">
          <span className="app-form-progress-title" style={{ color: 'var(--theme-text-secondary)' }}>
            Form Progress
          </span>
          <span className={`app-form-progress-percentage ${isComplete ? 'app-form-progress-percentage--complete' : ''}`}
                style={{ color: 'var(--theme-text-secondary)' }}>
            {isComplete && (
              <span className="app-form-progress-checkmark">✓</span>
            )}
            {Math.round(totalProgress)}%
          </span>
        </div>
        <div className="app-form-progress-bar">
          <div 
            className={`app-form-progress-fill ${isComplete ? 'app-form-progress-fill--complete' : ''}`}
            style={{ width: `${totalProgress}%` }}
          />
        </div>
        <div className="app-form-progress-details">
          {completedRequired < requiredFields.length && (
            <div className="app-form-progress-message" style={{ color: 'var(--theme-text-secondary)' }}>
              {requiredFields.length - completedRequired} required field{requiredFields.length - completedRequired !== 1 ? 's' : ''} remaining
            </div>
          )}
          {Object.keys(errors).length > 0 && (
            <div className="app-form-progress-errors">
              {Object.entries(errors).filter(([_, error]) => error).map(([field, error]) => (
                <div key={field} className="app-form-progress-error" style={{ color: 'rgb(239, 68, 68)' }}>
                  {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {error}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function useFormProgress(
  formData: Record<string, string>,
  errors: Record<string, string>,
  requiredFields: string[],
  optionalFields: string[]
) {
  let completedRequired = 0;
  let completedOptional = 0;
  
  // Check required fields
  requiredFields.forEach(field => {
    const value = formData[field];
    if (value && value.trim()) {
      // Check if field is valid
      if (field === 'email' && !validationPatterns.email.test(value)) {
        return;
      }
      completedRequired++;
    }
  });
  
  // Check optional fields
  optionalFields.forEach(field => {
    const value = formData[field];
    if (value && value.trim()) {
      // Check if field is valid (phone validation if needed)
      if (field === 'phone_number' && value) {
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly && !validationPatterns.phone.test(digitsOnly)) {
          return;
        }
      }
      completedOptional++;
    }
  });
  
  // Calculate progress: 70% for required fields, 30% for optional
  const requiredProgress = (completedRequired / requiredFields.length) * 70;
  const optionalProgress = optionalFields.length > 0 
    ? (completedOptional / optionalFields.length) * 30 
    : 0;
  const percentage = requiredProgress + optionalProgress;
  
  // Form is submittable when all required fields are valid and there are no errors
  const activeErrors = Object.entries(errors).filter(([_, error]) => error);
  const isSubmittable = completedRequired === requiredFields.length && activeErrors.length === 0;
  
  // Form is submittable and considered complete when all required fields are valid and there are no errors
  // This matches the create modal behavior exactly
  const isComplete = completedRequired === requiredFields.length && activeErrors.length === 0;

  return {
    percentage: Math.round(percentage),
    isSubmittable,
    isComplete,
    completedRequired,
    completedOptional,
    totalRequired: requiredFields.length,
    totalOptional: optionalFields.length,
    activeErrors
  };
}